import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Home } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { useMarketingTracker } from "../lib/marketingTracker";
import { browserTimezone } from "../lib/timezones";

/**
 * Phase 2B.1 — Public agent self-signup.
 *
 * Visible at theporchlight.io/signup. Posts to /api/agents/signup, which
 * creates a new admin user + their own site_content tenant and returns
 * an access_token. We stash that token and route the agent straight
 * into their fresh /admin portal.
 *
 * The slug field auto-derives from full name as the user types, but they
 * can override it. Availability is checked with a debounced ping.
 */
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");

export default function AgentSignup() {
  useMarketingTracker();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { refresh: refreshContent } = useContent();
  const [form, setForm] = useState({
    full_name: "", firm: "", email: "", phone: "", password: "", slug: "",
    pain_point: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-derive slug from name unless the user has explicitly typed in the slug field.
      if (k === "full_name" && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  // Debounced slug availability check
  useEffect(() => {
    const s = slugify(form.slug);
    if (!s) { setSlugStatus({ checking: false, available: null, reason: "" }); return; }
    setSlugStatus((p) => ({ ...p, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/agents/slug-available?slug=${encodeURIComponent(s)}`);
        setSlugStatus({ checking: false, available: !!data.available, reason: data.reason || "" });
      } catch {
        setSlugStatus({ checking: false, available: null, reason: "Couldn't check availability." });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.slug]);

  const passwordOk = form.password.length >= 8;
  const formValid =
    form.full_name.trim().length >= 2 &&
    form.firm.trim().length >= 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    passwordOk &&
    slugStatus.available !== false;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        firm: form.firm.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        slug: slugify(form.slug) || null,
        pain_point: form.pain_point.trim(),
        // Showing reminders fire in the agent's local time. Reading it from the
        // browser means a Dallas agent is on Central without being asked; the
        // backend falls back to a default if this is missing or nonsense.
        timezone: browserTimezone(),
      };
      const { data } = await api.post("/agents/signup", payload);
      // Persist token in the same place useAuth() looks
      try { localStorage.setItem("sellerhub_token", data.access_token); } catch {}
      // Refresh both auth state (so the new admin user appears in the layout)
      // and content state (so their branding replaces Erika's defaults in the
      // footer and elsewhere) before navigating.
      await Promise.all([refresh?.(), refreshContent?.()]);
      nav("/admin");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't create your account. Try again.");
      setBusy(false);
    }
  };

  const slugPreview = useMemo(() => slugify(form.slug) || "your-name", [form.slug]);

  return (
    <div data-testid="agent-signup-page" style={{ minHeight: "100vh", background: "var(--surface)" }}>
      <div className="container-x" style={{ padding: "32px 0 96px" }}>
        {/* Lightweight nav back to marketing */}
        <Link
          to="/for-agents"
          data-testid="signup-back-link"
          style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            color: "var(--text-muted)", textDecoration: "none", fontSize: 14, marginBottom: 32,
          }}
        >
          <span style={{
            width: 28, height: 28, borderRadius: 8,
            background: "var(--primary)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Home size={14} />
          </span>
          <span className="font-serif" style={{ fontSize: 18, color: "var(--text)", fontWeight: 500 }}>PorchLight</span>
        </Link>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", maxWidth: 540, margin: "0 auto" }}>
          <div>
            <span className="overline">Phase 2 · Agent signup</span>
            <h1 className="font-serif"
                style={{ fontSize: "clamp(34px, 5vw, 48px)", lineHeight: 1.05,
                         letterSpacing: "-0.015em", fontWeight: 500, margin: "10px 0 14px" }}>
              Make PorchLight <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>yours.</em>
            </h1>
            <p style={{ color: "var(--text-muted)", maxWidth: 460, marginBottom: 32, lineHeight: 1.55 }}>
              Six fields, your own white-labeled portal. Add your first seller in under two minutes.
            </p>

            <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Field label="Your full name" testid="signup-name">
                <input
                  data-testid="signup-name-input"
                  className="input-field"
                  required
                  value={form.full_name}
                  onChange={update("full_name")}
                  placeholder="Sarah Bennett"
                  autoComplete="name"
                />
              </Field>

              <Field label="Brokerage / firm" testid="signup-firm">
                <input
                  data-testid="signup-firm-input"
                  className="input-field"
                  required
                  value={form.firm}
                  onChange={update("firm")}
                  placeholder="Bennett Realty Group"
                  autoComplete="organization"
                />
              </Field>

              <Field label="Work email" testid="signup-email">
                <input
                  data-testid="signup-email-input"
                  className="input-field"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="sarah@bennettrealty.com"
                  autoComplete="email"
                />
              </Field>

              <Field
                label="Mobile (optional)"
                hint="Sellers see this on prep questions and email signatures."
                testid="signup-phone"
              >
                <input
                  data-testid="signup-phone-input"
                  className="input-field"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 234-1100"
                  autoComplete="tel"
                />
              </Field>

              <Field
                label="Your portal address"
                hint={
                  <span>
                    Your sellers will visit{" "}
                    <code style={{
                      background: "var(--surface-alt)", padding: "2px 6px", borderRadius: 6,
                      fontSize: 13, color: "var(--text)",
                    }}>
                      theporchlight.io/{slugPreview}
                    </code>
                  </span>
                }
                testid="signup-slug"
              >
                <input
                  data-testid="signup-slug-input"
                  className="input-field"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); update("slug")(e); }}
                  placeholder="sarah-bennett"
                  autoComplete="off"
                  spellCheck={false}
                  style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: 14 }}
                />
                <SlugStatusLine status={slugStatus} value={form.slug} />
              </Field>

              <Field
                label="What's your biggest frustration with seller management today?"
                hint="Optional, but this is the question we'll quote back to you. The more honest, the better."
                testid="signup-pain"
              >
                <textarea
                  data-testid="signup-pain-input"
                  className="input-field"
                  rows={3}
                  value={form.pain_point}
                  onChange={update("pain_point")}
                  placeholder="The same questions on repeat… disorganized text threads… clients losing track of important dates…"
                  style={{ resize: "vertical", fontFamily: "inherit" }}
                />
              </Field>

              <Field
                label="Password"
                hint={passwordOk ? "Looks good." : "8+ characters."}
                testid="signup-password"
              >
                <input
                  data-testid="signup-password-input"
                  className="input-field"
                  required
                  type="password"
                  value={form.password}
                  onChange={update("password")}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
              </Field>

              {err && (
                <div data-testid="signup-error"
                     style={{ color: "var(--error)", fontSize: 14,
                              padding: "10px 14px", background: "rgba(220, 50, 50, 0.06)",
                              borderRadius: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <AlertCircle size={16} />
                  {err}
                </div>
              )}

              <button
                data-testid="signup-submit-btn"
                className="btn-primary"
                type="submit"
                disabled={busy || !formValid}
                style={{ justifyContent: "center", marginTop: 12, opacity: (busy || !formValid) ? 0.55 : 1 }}
              >
                {busy ? (<><Loader2 size={16} className="spin" /> Creating your portal…</>) : "Create my portal"}
              </button>

              <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", marginTop: 4 }}>
                Already have an account?{" "}
                <Link data-testid="signup-login-link" to="/login" style={{ color: "var(--secondary)", fontWeight: 500 }}>
                  Log in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 500,
                      letterSpacing: "0.01em", marginBottom: 6, color: "var(--text)" }}>
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 6, lineHeight: 1.4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function SlugStatusLine({ status, value }) {
  if (!value) return null;
  if (status.checking) {
    return (
      <div data-testid="signup-slug-status" style={{ display: "flex", alignItems: "center", gap: 6,
                                                     fontSize: 12.5, color: "var(--text-muted)", marginTop: 6 }}>
        <Loader2 size={12} className="spin" /> Checking…
      </div>
    );
  }
  if (status.available === true) {
    return (
      <div data-testid="signup-slug-status" style={{ display: "flex", alignItems: "center", gap: 6,
                                                     fontSize: 12.5, color: "#3F8B5A", marginTop: 6 }}>
        <CheckCircle2 size={12} /> Available
      </div>
    );
  }
  if (status.available === false) {
    return (
      <div data-testid="signup-slug-status" style={{ display: "flex", alignItems: "center", gap: 6,
                                                     fontSize: 12.5, color: "var(--error)", marginTop: 6 }}>
        <AlertCircle size={12} /> {status.reason || "Not available."}
      </div>
    );
  }
  return null;
}
